import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, Calendar, LogOut, User, Clock, CheckCircle2,
  MapPin, Phone, Camera, ChevronRight, Building2, AlertCircle, X,
  ChevronLeft, Printer, Palmtree, Plus, Trash2, Loader2, XCircle, Video, Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { collaboratorApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { FooterMinimal } from '../../components/Footer';
import NewVersionToast from '../../components/NewVersionToast';
import { getSlotTypeInfo, slotTypeBadgeClasses, slotTypeEmoji } from '../../lib/slotTypeLabel';
import NotificationsPage from '../NotificationsPage';
import { useTheme } from '../../context/ThemeContext';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO, differenceInDays, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'fr': fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, variant = 'default' }) => (
  <Card className="card-hover">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-heading font-semibold mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          variant === 'warning' ? 'bg-amber-100' : variant === 'success' ? 'bg-green-100' : 'bg-primary/10'
        }`}>
          <Icon className={`w-6 h-6 ${
            variant === 'warning' ? 'text-amber-600' : variant === 'success' ? 'text-green-600' : 'text-primary'
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// My Appointments (RDV) - for commercials/collaborators
function MyAppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending | confirmed | all
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [decisionType, setDecisionType] = useState('accept');
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await collaboratorApi.getMyAppointments();
      setAppointments(res.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDecision = async () => {
    if (!decisionTarget) return;
    setIsSubmitting(true);
    try {
      await collaboratorApi.decideAppointment(decisionTarget.id, decisionType, decisionType === 'reject' ? rejectReason : null);
      toast.success(decisionType === 'accept' ? 'RDV accepté' : 'RDV refusé');
      setDecisionTarget(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = appointments.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return a.status === 'pending' && a.validation_mode === 'manual';
    if (filter === 'confirmed') return ['confirmed', 'approved'].includes(a.status);
    return true;
  });

  const counts = {
    pending: appointments.filter(a => a.status === 'pending' && a.validation_mode === 'manual').length,
    confirmed: appointments.filter(a => ['confirmed', 'approved'].includes(a.status)).length,
    all: appointments.length,
  };

  return (
    <div className="space-y-5" data-testid="my-appointments-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
            <Briefcase className="w-5 h-5" />
          </span>
          Mes Rendez-vous
        </h1>
        <p className="text-muted-foreground mt-1">
          Acceptez ou refusez les RDV qui vous sont assignés
        </p>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: 'pending', label: 'À valider', color: 'bg-orange-500' },
          { v: 'confirmed', label: 'Confirmés', color: 'bg-emerald-500' },
          { v: 'all', label: 'Tous', color: 'bg-gray-700' },
        ].map(({ v, label, color }) => (
          <button
            key={v}
            type="button"
            onClick={() => setFilter(v)}
            data-testid={`my-appt-filter-${v}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
              filter === v
                ? `${color} text-white border-transparent shadow-md`
                : 'bg-white border-border text-foreground hover:border-foreground/20'
            }`}
          >
            <span>{label}</span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
              filter === v ? 'bg-white/20' : 'bg-muted text-muted-foreground'
            }`}>
              {counts[v]}
            </span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card><CardContent className="p-6">Chargement…</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            Aucun RDV {filter === 'pending' ? 'à valider' : filter === 'confirmed' ? 'confirmé' : ''}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((a) => {
            const isVideo = a.meeting_type === 'online' || a.meeting_type === 'video';
            const isPending = a.status === 'pending';
            return (
              <Card key={a.id} data-testid={`my-appt-card-${a.id}`} className="overflow-hidden">
                <div className={`h-1.5 ${isVideo ? 'bg-indigo-500' : 'bg-blue-500'}`} />
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {isVideo ? (
                          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 gap-1">
                            <Video className="w-3 h-3" />
                            Visio
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1">
                            <MapPin className="w-3 h-3" />
                            Présentiel
                          </Badge>
                        )}
                        {a.validation_mode === 'auto' && (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Auto-validé
                          </Badge>
                        )}
                      </div>
                      <p className="font-semibold text-lg leading-tight">{a.client_name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {a.client_email}
                      </p>
                    </div>
                    <Badge className={
                      a.status === 'confirmed' || a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      a.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      a.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                      'bg-orange-100 text-orange-700'
                    }>
                      {a.status === 'pending' ? 'En attente' :
                        a.status === 'confirmed' ? 'Confirmé' :
                        a.status === 'approved' ? 'Approuvé' :
                        a.status === 'rejected' ? 'Refusé' :
                        a.status === 'cancelled' ? 'Annulé' : a.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{a.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{a.start_time || a.time_slot} ({a.duration_minutes || 60} min)</span>
                    </div>
                    {a.client_phone && (
                      <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                        <Phone className="w-4 h-4" />
                        <span>{a.client_phone}</span>
                      </div>
                    )}
                  </div>

                  {a.notes && (
                    <p className="text-sm bg-muted/50 rounded-lg p-3 italic">
                      "{a.notes}"
                    </p>
                  )}

                  {isPending && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        className="flex-1"
                        onClick={() => { setDecisionTarget(a); setDecisionType('accept'); }}
                        data-testid={`accept-appt-${a.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Accepter
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => { setDecisionTarget(a); setDecisionType('reject'); }}
                        data-testid={`reject-appt-${a.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Refuser
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!decisionTarget} onOpenChange={(o) => !o && setDecisionTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionType === 'accept' ? 'Accepter ce RDV ?' : 'Refuser ce RDV ?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {decisionType === 'accept'
                ? `Le client ${decisionTarget?.client_name} sera notifié par email.`
                : `Le client ${decisionTarget?.client_name} sera informé du refus.`}
            </p>
            {decisionType === 'reject' && (
              <div className="space-y-2">
                <Label>Motif (optionnel)</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex: Indisponible à cette date"
                  data-testid="reject-reason-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionTarget(null)} disabled={isSubmitting}>Annuler</Button>
            <Button
              onClick={handleDecision}
              disabled={isSubmitting}
              className={decisionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-decision-btn"
            >
              {isSubmitting ? 'En cours…' : (decisionType === 'accept' ? 'Confirmer l\'acceptation' : 'Confirmer le refus')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Dashboard Overview
function DashboardOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayBookings, setTodayBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, todayRes] = await Promise.all([
          collaboratorApi.getStats(),
          collaboratorApi.getTodayBookings()
        ]);
        setStats(statsRes.data);
        setTodayBookings(todayRes.data);
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-muted rounded w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 stagger-children">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Bonjour, {user?.name}</h1>
        <p className="text-muted-foreground mt-1">Voici vos rendez-vous du jour</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Rendez-vous aujourd'hui"
          value={stats?.today_bookings || 0}
          icon={Calendar}
          variant={stats?.today_bookings > 0 ? 'warning' : 'default'}
        />
        <StatCard
          title="À venir"
          value={stats?.upcoming_bookings || 0}
          icon={Clock}
        />
        <StatCard
          title="Terminés ce mois"
          value={stats?.completed_this_month || 0}
          icon={CheckCircle2}
          variant="success"
        />
      </div>

      {/* Today's Bookings */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Rendez-vous d'aujourd'hui
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayBookings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun rendez-vous prévu aujourd'hui</p>
            </div>
          ) : (
            <div className="space-y-4">
              {todayBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Booking Card Component with Photo Upload
function BookingCard({ booking, showActions = true, onStatusUpdate }) {
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState(null);
  const [afterPhotoPreview, setAfterPhotoPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const beforePhotoRef = React.useRef(null);
  const afterPhotoRef = React.useRef(null);

  const handleBeforePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBeforePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setBeforePhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAfterPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAfterPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setAfterPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleStartIntervention = async () => {
    setIsSubmitting(true);
    try {
      // Upload before photo if provided
      if (beforePhoto) {
        await collaboratorApi.uploadInterventionPhoto(booking.id, beforePhoto, 'before', 'Photo avant intervention', true);
      }
      
      // Update status to in_progress
      await collaboratorApi.updateBookingStatus(booking.id, 'in_progress', booking.booking_type);
      toast.success('Prestation démarrée ! Bonne intervention.');
      setIsStartDialogOpen(false);
      setBeforePhoto(null);
      setBeforePhotoPreview(null);
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      toast.error('Erreur lors du démarrage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteIntervention = async () => {
    setIsSubmitting(true);
    try {
      // Upload after photo if provided
      if (afterPhoto) {
        await collaboratorApi.uploadInterventionPhoto(booking.id, afterPhoto, 'after', 'Photo après intervention', true);
      }
      
      // Update status to completed
      await collaboratorApi.updateBookingStatus(booking.id, 'completed', booking.booking_type);
      toast.success('Intervention terminée avec succès !');
      setIsCompleteDialogOpen(false);
      setAfterPhoto(null);
      setAfterPhotoPreview(null);
      setNotes('');
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      toast.error('Erreur lors de la finalisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Confirmé</Badge>;
      case 'in_progress':
        return <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200">En cours</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Terminé</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card className="card-hover">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getStatusBadge(booking.status)}
              <Badge variant="outline">
                {booking.booking_type === 'punctual' ? 'Ponctuel' : 'Abonnement'}
              </Badge>
            </div>
            
            {/* Client Info */}
            <div className="space-y-1">
              <p className="font-medium text-lg">
                {booking.client?.name || booking.client_name || 'Client'}
              </p>
              
              {(booking.client?.phone || booking.client_phone) && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {booking.client?.phone || booking.client_phone}
                </p>
              )}
              
              {(booking.client?.address || booking.client_address) && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {booking.client?.address || booking.client_address}
                </p>
              )}
            </div>

            {/* Time info */}
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {new Date(booking.date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </span>
              {booking.start_time && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  {booking.start_time} {booking.end_time && `- ${booking.end_time}`}
                </span>
              )}
              {booking.slot_type && (() => {
                const info = getSlotTypeInfo(booking);
                return (
                  <Badge
                    variant="outline"
                    className={`${slotTypeBadgeClasses[info.kind]} gap-1.5 text-xs`}
                    data-testid={`collab-slot-badge-${booking.id}`}
                  >
                    <span>{slotTypeEmoji[info.kind]}</span>
                    <span>{info.label}</span>
                  </Badge>
                );
              })()}
            </div>

            {booking.service_name && (
              <p className="text-sm text-primary mt-2 font-medium">{booking.service_name}</p>
            )}
          </div>

          {/* Actions */}
          {showActions && booking.status !== 'completed' && (
            <div className="flex flex-wrap gap-2">
              {/* Démarrer - For confirmed bookings */}
              {booking.status === 'confirmed' && (
                <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`start-booking-${booking.id}`}
                    >
                      <Camera className="w-4 h-4 mr-1" />
                      Démarrer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading flex items-center gap-2">
                        <Camera className="w-5 h-5 text-blue-600" />
                        Démarrer la prestation
                      </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Prenez une photo "avant" du chantier pour documenter l'état initial.
                    </p>
                    <div className="space-y-4 mt-4">
                      <input
                        type="file"
                        ref={beforePhotoRef}
                        accept="image/*"
                        capture="environment"
                        onChange={handleBeforePhotoSelect}
                        className="hidden"
                      />
                      
                      {beforePhotoPreview ? (
                        <div className="relative">
                          <img 
                            src={beforePhotoPreview} 
                            alt="Preview" 
                            className="w-full h-48 object-cover rounded-lg border"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              setBeforePhoto(null);
                              setBeforePhotoPreview(null);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                          onClick={() => beforePhotoRef.current?.click()}
                        >
                          <Camera className="w-10 h-10 mx-auto text-blue-400 mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Cliquez pour prendre une photo "avant"
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setIsStartDialogOpen(false);
                            setBeforePhoto(null);
                            setBeforePhotoPreview(null);
                          }}
                        >
                          Annuler
                        </Button>
                        <Button
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={handleStartIntervention}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'En cours...' : (beforePhoto ? 'Démarrer avec photo' : 'Démarrer sans photo')}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              
              {/* Terminer - For in_progress bookings */}
              {booking.status === 'in_progress' && (
                <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`complete-booking-${booking.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Terminer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        Terminer la prestation
                      </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Ajoutez une photo "après" du travail réalisé pour finaliser cette intervention.
                    </p>
                    <div className="space-y-4 mt-4">
                      <input
                        type="file"
                        ref={afterPhotoRef}
                        accept="image/*"
                        capture="environment"
                        onChange={handleAfterPhotoSelect}
                        className="hidden"
                      />
                      
                      {afterPhotoPreview ? (
                        <div className="relative">
                          <img 
                            src={afterPhotoPreview} 
                            alt="Preview" 
                            className="w-full h-48 object-cover rounded-lg border"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              setAfterPhoto(null);
                              setAfterPhotoPreview(null);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="border-2 border-dashed border-green-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                          onClick={() => afterPhotoRef.current?.click()}
                        >
                          <Camera className="w-10 h-10 mx-auto text-green-400 mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Cliquez pour prendre une photo "après"
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Notes (optionnel)</Label>
                        <Textarea
                          placeholder="Commentaires sur l'intervention..."
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={2}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setIsCompleteDialogOpen(false);
                            setAfterPhoto(null);
                            setAfterPhotoPreview(null);
                            setNotes('');
                          }}
                        >
                          Annuler
                        </Button>
                        <Button
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={handleCompleteIntervention}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'En cours...' : (afterPhoto ? 'Terminer avec photo' : 'Terminer sans photo')}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// All Bookings Page
function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await collaboratorApi.getBookings();
      setBookings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const today = new Date().toISOString().split('T')[0];
  
  const filteredBookings = bookings.filter(b => {
    if (filter === 'upcoming') return b.date >= today && b.status !== 'completed';
    if (filter === 'completed') return b.status === 'completed';
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Mes rendez-vous</h1>
        <p className="text-muted-foreground mt-1">Consultez et gérez vos interventions</p>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'upcoming' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('upcoming')}
        >
          À venir
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
        >
          Terminés
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          Tous
        </Button>
      </div>

      {/* Bookings List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredBookings.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucun rendez-vous trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              showActions={booking.status !== 'completed'}
              onStatusUpdate={fetchBookings}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Planning Page - Calendar view for collaborator
function PlanningPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('week'); // 'week' or 'month'
  
  // Photo states
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState(null);
  const [afterPhotoPreview, setAfterPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const beforePhotoRef = React.useRef(null);
  const afterPhotoRef = React.useRef(null);

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await collaboratorApi.getBookings();
      setBookings(response.data);
      
      // Convert bookings to calendar events
      const calendarEvents = response.data
        .filter(b => b.date && ['confirmed', 'in_progress', 'pending'].includes(b.status))
        .map(booking => {
          const startHour = booking.start_time || (booking.slot_period === 'afternoon' ? '14:00' : '08:00');
          const endHour = booking.end_time || (booking.slot_type === 'full_day' ? '18:00' : (booking.slot_period === 'afternoon' ? '18:00' : '12:00'));
          const isInProgress = booking.status === 'in_progress';
          
          return {
            id: booking.id,
            title: isInProgress 
              ? `🔄 ${booking.client?.name || booking.client_name || 'Client'}`
              : `${booking.client?.name || booking.client_name || 'Client'}`,
            start: new Date(`${booking.date}T${startHour}:00`),
            end: new Date(`${booking.date}T${endHour}:00`),
            resource: booking,
            status: booking.status,
            type: booking.booking_type
          };
        });
      
      setEvents(calendarEvents);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleBeforePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBeforePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setBeforePhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAfterPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAfterPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setAfterPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleStartIntervention = async () => {
    if (!selectedEvent) return;
    setIsSubmitting(true);
    try {
      if (beforePhoto) {
        await collaboratorApi.uploadInterventionPhoto(selectedEvent.id, beforePhoto, 'before', 'Photo avant intervention', true);
      }
      await collaboratorApi.updateBookingStatus(selectedEvent.id, 'in_progress', selectedEvent.type);
      toast.success('Prestation démarrée ! Bonne intervention.');
      setIsStartDialogOpen(false);
      setSelectedEvent(null);
      setBeforePhoto(null);
      setBeforePhotoPreview(null);
      fetchBookings();
    } catch (error) {
      toast.error('Erreur lors du démarrage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteIntervention = async () => {
    if (!selectedEvent) return;
    setIsSubmitting(true);
    try {
      if (afterPhoto) {
        await collaboratorApi.uploadInterventionPhoto(selectedEvent.id, afterPhoto, 'after', 'Photo après intervention', true);
      }
      await collaboratorApi.updateBookingStatus(selectedEvent.id, 'completed', selectedEvent.type);
      toast.success('Intervention terminée avec succès !');
      setIsCompleteDialogOpen(false);
      setSelectedEvent(null);
      setAfterPhoto(null);
      setAfterPhotoPreview(null);
      fetchBookings();
    } catch (error) {
      toast.error('Erreur lors de la finalisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventStyleGetter = (event) => {
    let style = {
      borderRadius: '6px',
      opacity: 0.95,
      border: 'none',
      fontSize: '12px',
      padding: '2px 6px'
    };
    
    if (event.status === 'in_progress') {
      style.backgroundColor = '#06b6d4'; // cyan
      style.color = '#fff';
      style.border = '2px solid #0891b2';
    } else if (event.status === 'pending') {
      style.backgroundColor = '#f97316'; // orange
      style.color = '#fff';
    } else if (event.type === 'punctual') {
      style.backgroundColor = '#8b5cf6'; // purple
      style.color = '#fff';
    } else {
      style.backgroundColor = '#3b82f6'; // blue
      style.color = '#fff';
    }
    
    return { style };
  };

  const navigateDate = (direction) => {
    if (view === 'week') {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 7) : subDays(selectedDate, 7));
    } else {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 30) : subDays(selectedDate, 30));
    }
  };

  // Get stats for the current period
  const periodStart = view === 'week' ? startOfWeek(selectedDate, { weekStartsOn: 1 }) : startOfMonth(selectedDate);
  const periodEnd = view === 'week' ? addDays(periodStart, 6) : endOfMonth(selectedDate);
  const periodEvents = events.filter(e => e.start >= periodStart && e.start <= periodEnd);
  const confirmedCount = periodEvents.filter(e => e.status === 'confirmed').length;
  const inProgressCount = periodEvents.filter(e => e.status === 'in_progress').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Mon Planning</h1>
          <p className="text-muted-foreground mt-1">Visualisez vos interventions sur le calendrier</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setView('week')} className={view === 'week' ? 'bg-primary text-primary-foreground' : ''}>
            Semaine
          </Button>
          <Button variant="outline" size="sm" onClick={() => setView('month')} className={view === 'month' ? 'bg-primary text-primary-foreground' : ''}>
            Mois
          </Button>
        </div>
      </div>

      {/* Navigation and Stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-muted/30 p-4 rounded-xl">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigateDate('prev')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center min-w-[200px]">
            <h2 className="font-semibold">
              {view === 'week' 
                ? `Semaine du ${format(periodStart, 'd MMMM', { locale: fr })}`
                : format(selectedDate, 'MMMM yyyy', { locale: fr })}
            </h2>
          </div>
          <Button variant="outline" size="icon" onClick={() => navigateDate('next')}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())}>
            Aujourd'hui
          </Button>
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Confirmé ({confirmedCount})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
            <span>En cours ({inProgressCount})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span>Ponctuel</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      {isLoading ? (
        <div className="h-[600px] bg-muted rounded-xl animate-pulse" />
      ) : (
        <Card>
          <CardContent className="p-4">
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              view={view}
              date={selectedDate}
              onNavigate={setSelectedDate}
              onView={setView}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={(event) => setSelectedEvent(event)}
              messages={{
                today: "Aujourd'hui",
                previous: 'Précédent',
                next: 'Suivant',
                month: 'Mois',
                week: 'Semaine',
                day: 'Jour',
                agenda: 'Agenda',
                noEventsInRange: 'Aucune intervention sur cette période',
              }}
              formats={{
                dayFormat: (date, culture, localizer) => format(date, 'EEE d', { locale: fr }),
                dayHeaderFormat: (date, culture, localizer) => format(date, 'EEEE d MMMM', { locale: fr }),
                dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                  `${format(start, 'd MMM', { locale: fr })} - ${format(end, 'd MMM yyyy', { locale: fr })}`,
              }}
              min={new Date(2026, 0, 1, 7, 0)}
              max={new Date(2026, 0, 1, 19, 0)}
              toolbar={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEvent && !isStartDialogOpen && !isCompleteDialogOpen} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.status === 'in_progress' ? (
                <Badge className="bg-cyan-100 text-cyan-700">En cours</Badge>
              ) : (
                <Badge className="bg-blue-100 text-blue-700">Confirmé</Badge>
              )}
              {selectedEvent?.type === 'punctual' ? 'Ponctuel' : 'Abonnement'}
            </DialogTitle>
            <DialogDescription>Détails de l'intervention</DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <p className="font-semibold text-lg">{selectedEvent.resource?.client?.name || selectedEvent.resource?.client_name}</p>
                {selectedEvent.resource?.client?.phone && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {selectedEvent.resource?.client?.phone}
                  </p>
                )}
                {selectedEvent.resource?.client?.address && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedEvent.resource?.client?.address}
                  </p>
                )}
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                <p className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {format(selectedEvent.start, 'EEEE d MMMM yyyy', { locale: fr })}
                </p>
                <p className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {format(selectedEvent.start, 'HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
                </p>
              </div>
              
              {selectedEvent.resource?.service_name && (
                <p className="text-sm text-primary font-medium">{selectedEvent.resource.service_name}</p>
              )}
              
              {/* Actions */}
              <div className="space-y-2 pt-4 border-t">
                {selectedEvent.status === 'confirmed' && (
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => setIsStartDialogOpen(true)}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Démarrer la prestation
                  </Button>
                )}
                
                {selectedEvent.status === 'in_progress' && (
                  <>
                    <div className="p-2 bg-cyan-50 rounded-lg border border-cyan-200">
                      <p className="text-sm text-cyan-700 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Prestation en cours...
                      </p>
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => setIsCompleteDialogOpen(true)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Terminer la prestation
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Start Dialog */}
      <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Démarrer la prestation
            </DialogTitle>
            <DialogDescription>
              Prenez une photo "avant" du chantier pour documenter l'état initial.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <input
              type="file"
              ref={beforePhotoRef}
              accept="image/*"
              capture="environment"
              onChange={handleBeforePhotoSelect}
              className="hidden"
            />
            
            {beforePhotoPreview ? (
              <div className="relative">
                <img src={beforePhotoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border" />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => { setBeforePhoto(null); setBeforePhotoPreview(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                onClick={() => beforePhotoRef.current?.click()}
              >
                <Camera className="w-10 h-10 mx-auto text-blue-400 mb-2" />
                <p className="text-sm text-muted-foreground">Cliquez pour prendre une photo "avant"</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setIsStartDialogOpen(false); setBeforePhoto(null); setBeforePhotoPreview(null); }}>
                Annuler
              </Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleStartIntervention} disabled={isSubmitting}>
                {isSubmitting ? 'En cours...' : (beforePhoto ? 'Démarrer avec photo' : 'Démarrer sans photo')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Terminer la prestation
            </DialogTitle>
            <DialogDescription>
              Ajoutez une photo "après" du travail réalisé pour finaliser cette intervention.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <input
              type="file"
              ref={afterPhotoRef}
              accept="image/*"
              capture="environment"
              onChange={handleAfterPhotoSelect}
              className="hidden"
            />
            
            {afterPhotoPreview ? (
              <div className="relative">
                <img src={afterPhotoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border" />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => { setAfterPhoto(null); setAfterPhotoPreview(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-green-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                onClick={() => afterPhotoRef.current?.click()}
              >
                <Camera className="w-10 h-10 mx-auto text-green-400 mb-2" />
                <p className="text-sm text-muted-foreground">Cliquez pour prendre une photo "après"</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setIsCompleteDialogOpen(false); setAfterPhoto(null); setAfterPhotoPreview(null); }}>
                Annuler
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleCompleteIntervention} disabled={isSubmitting}>
                {isSubmitting ? 'En cours...' : (afterPhoto ? 'Terminer avec photo' : 'Terminer sans photo')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Profile Page
function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await collaboratorApi.getProfile();
        setProfile(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (isLoading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-40 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Mon profil</h1>
        <p className="text-muted-foreground mt-1">Vos informations personnelles</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-semibold">{profile?.name}</h2>
              {profile?.email && (
                <p className="text-muted-foreground mt-1">{profile.email}</p>
              )}
              {profile?.phone && (
                <p className="text-muted-foreground flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4" /> {profile.phone}
                </p>
              )}
              
              {profile?.specialties?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile.specialties.map((spec, i) => (
                    <Badge key={i} variant="secondary">{spec}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Info */}
      {profile?.company && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Mon entreprise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{profile.company.name}</p>
            {profile.company.email && (
              <p className="text-sm text-muted-foreground mt-1">{profile.company.email}</p>
            )}
            {profile.company.phone && (
              <p className="text-sm text-muted-foreground mt-1">{profile.company.phone}</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Besoin de modifier vos informations ?</p>
            <p className="text-sm text-amber-700">
              Contactez votre entreprise pour mettre à jour votre profil.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Logo component (reduced by 30%)
const Logo = ({ className = "h-6" }) => {
  const { logoUrl } = useTheme();
  const defaultLogo = "/logo.png";
  
  return (
    <img 
      src={logoUrl || defaultLogo} 
      alt="ResaWork" 
      className={className}
    />
  );
};

// ============ MY LEAVES PAGE ============
const LEAVE_TYPES = [
  { value: 'vacation', label: 'Congés payés', color: 'bg-green-100 text-green-700' },
  { value: 'sick', label: 'Maladie', color: 'bg-red-100 text-red-700' },
  { value: 'training', label: 'Formation', color: 'bg-blue-100 text-blue-700' },
  { value: 'personal', label: 'Personnel', color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Autre', color: 'bg-gray-100 text-gray-700' },
];

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
};

function MyLeavesPage() {
  const [leaves, setLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newLeave, setNewLeave] = useState({
    start_date: '', end_date: '', leave_type: 'vacation', reason: ''
  });

  const fetchLeaves = async () => {
    try {
      const res = await collaboratorApi.getMyLeaves();
      setLeaves(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchLeaves(); }, []);

  const getLeaveTypeConfig = (type) => LEAVE_TYPES.find(t => t.value === type) || LEAVE_TYPES[4];
  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const calculateDays = (start, end) => differenceInDays(parseISO(end), parseISO(start)) + 1;

  const formatDateRange = (start, end) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    if (start === end) return format(startDate, 'd MMMM yyyy', { locale: fr });
    return `${format(startDate, 'd MMM', { locale: fr })} → ${format(endDate, 'd MMM yyyy', { locale: fr })}`;
  };

  const handleSubmitRequest = async () => {
    if (!newLeave.start_date || !newLeave.end_date) {
      toast.error('Veuillez sélectionner les dates');
      return;
    }
    setIsSubmitting(true);
    try {
      await collaboratorApi.requestLeave(newLeave);
      toast.success('Demande envoyée ! En attente de validation.');
      setIsDialogOpen(false);
      setNewLeave({ start_date: '', end_date: '', leave_type: 'vacation', reason: '' });
      fetchLeaves();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelRequest = async (leaveId) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await collaboratorApi.cancelLeaveRequest(leaveId);
      toast.success('Demande annulée');
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Separate leaves by status
  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const approvedLeaves = leaves.filter(l => l.status === 'approved' && isAfter(parseISO(l.end_date), new Date()));
  const pastLeaves = leaves.filter(l => l.status !== 'pending' && (l.status === 'rejected' || !isAfter(parseISO(l.end_date), new Date())));

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Mes Congés & Absences</h1>
          <p className="text-muted-foreground mt-1">Consultez et demandez vos congés ou absences</p>
        </div>
        <Button className="rounded-full" onClick={() => setIsDialogOpen(true)} data-testid="request-leave-btn">
          <Plus className="w-4 h-4 mr-2" />
          Demander un congé / absence
        </Button>
      </div>

      {/* Pending requests */}
      {pendingLeaves.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2 text-amber-700">
              <Clock className="w-4 h-4" />
              En attente de validation ({pendingLeaves.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingLeaves.map(leave => {
              const typeConfig = getLeaveTypeConfig(leave.leave_type);
              return (
                <div key={leave.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                    <div>
                      <p className="font-medium">{formatDateRange(leave.start_date, leave.end_date)}</p>
                      <p className="text-sm text-muted-foreground">{calculateDays(leave.start_date, leave.end_date)} jour(s) - {leave.reason || 'Sans motif'}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleCancelRequest(leave.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Annuler
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Approved upcoming */}
      {approvedLeaves.length > 0 && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              Congés à venir ({approvedLeaves.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {approvedLeaves.map(leave => {
              const typeConfig = getLeaveTypeConfig(leave.leave_type);
              return (
                <div key={leave.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                    <div>
                      <p className="font-medium">{formatDateRange(leave.start_date, leave.end_date)}</p>
                      <p className="text-sm text-muted-foreground">{calculateDays(leave.start_date, leave.end_date)} jour(s)</p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Approuvé</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Historique</CardTitle>
        </CardHeader>
        <CardContent>
          {pastLeaves.length === 0 && pendingLeaves.length === 0 && approvedLeaves.length === 0 ? (
            <div className="p-8 text-center">
              <Palmtree className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Aucun congé enregistré</p>
              <Button className="mt-4 rounded-full" onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Faire une demande
              </Button>
            </div>
          ) : pastLeaves.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun historique</p>
          ) : (
            <div className="space-y-2">
              {pastLeaves.slice(0, 10).map(leave => {
                const typeConfig = getLeaveTypeConfig(leave.leave_type);
                const statusConfig = getStatusConfig(leave.status);
                const StatusIcon = statusConfig.icon;
                return (
                  <div key={leave.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg opacity-70">
                    <div className="flex items-center gap-3">
                      <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                      <div>
                        <p className="text-sm">{formatDateRange(leave.start_date, leave.end_date)}</p>
                        {leave.review_comment && (
                          <p className="text-xs text-muted-foreground">"{leave.review_comment}"</p>
                        )}
                      </div>
                    </div>
                    <Badge className={statusConfig.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-600" />
              Demander un congé ou une absence
            </DialogTitle>
            <DialogDescription>
              Votre demande sera envoyée à l'entreprise pour validation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de congé</Label>
              <Select value={newLeave.leave_type} onValueChange={(v) => setNewLeave({...newLeave, leave_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input 
                  type="date" 
                  value={newLeave.start_date}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => setNewLeave({...newLeave, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input 
                  type="date" 
                  value={newLeave.end_date}
                  min={newLeave.start_date || format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => setNewLeave({...newLeave, end_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Motif (optionnel)</Label>
              <Textarea 
                placeholder="Ex: Vacances en famille..."
                value={newLeave.reason}
                onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
            <Button 
              onClick={handleSubmitRequest} 
              disabled={isSubmitting || !newLeave.start_date || !newLeave.end_date}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Dashboard Component
export default function CollaboratorDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/collaborator', icon: LayoutDashboard, label: 'Accueil', end: true },
    { path: '/collaborator/planning', icon: Calendar, label: 'Planning' },
    { path: '/collaborator/appointments', icon: Briefcase, label: 'Mes RDV' },
    { path: '/collaborator/bookings', icon: Clock, label: 'Interventions' },
    { path: '/collaborator/leaves', icon: Palmtree, label: 'Congés & Absences' },
    { path: '/collaborator/profile', icon: User, label: 'Profil' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Dark "Command Bar" */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[hsl(222_47%_10%)] border-r border-[hsl(222_30%_18%)] z-50 hidden md:flex flex-col text-slate-200">
        <div className="p-5 border-b border-[hsl(222_30%_18%)]">
          <div className="bg-white/95 rounded-md px-2 py-1.5 inline-block">
            <Logo className="h-7" />
          </div>
          <p className="text-xs text-slate-400 mt-2.5">Espace Collaborateur</p>
        </div>
        
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground font-semibold shadow-[0_4px_12px_-4px_hsl(var(--primary)/0.6)]'
                    : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100'
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        
        <div className="p-3 border-t border-[hsl(222_30%_18%)]">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-9 h-9 bg-primary/15 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-slate-100 text-sm">{user?.name}</p>
              <p className="text-xs text-slate-500">Collaborateur</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-red-300 hover:bg-red-500/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <Logo className="h-8" />
        <div className="flex items-center gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `p-2 rounded-lg ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`
              }
            >
              <item.icon className="w-5 h-5" />
            </NavLink>
          ))}
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 pt-16 md:pt-0 min-h-screen">
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          <Routes>
            <Route path="/" element={<DashboardOverview />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/appointments" element={<MyAppointmentsPage />} />
            <Route path="/bookings" element={<BookingsPage />} />
            <Route path="/leaves" element={<MyLeavesPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
          </Routes>
        </div>
        <FooterMinimal />
      </main>
      {/* In-app new version notification */}
      <NewVersionToast />
    </div>
  );
}
